import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useI18n } from "@/i18n/I18nContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1593113616348-85c32d202799?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxwdWVydG8lMjBtYWRlcm8lMjBidWVub3MlMjBhaXJlcyUyMHRvd2VyJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc5MDYwMjAxN3ww&ixlib=rb-4.1.0&q=85";

const line = {
  hidden: { y: "108%" },
  show: (i = 0) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.1 + i * 0.12, ease: [0.2, 0.7, 0.2, 1] },
  }),
};

export default function Hero() {
  const { t } = useI18n();
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });

  const onMouseMove = (e) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ rx: -py * 6, ry: px * 8 });
  };
  const onLeave = () => setTilt({ rx: 0, ry: 0 });

  useEffect(() => {
    // Preload
    const img = new Image();
    img.src = HERO_IMG;
  }, []);

  return (
    <section className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden" data-testid="hero">
      <div className="container-x relative">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="eyebrow"
        >
          {t("hero.eyebrow")}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mt-6">
          <h1 className="lg:col-span-8 font-serif-display text-[13vw] leading-[0.92] sm:text-[9vw] lg:text-[7.6vw] tracking-tight text-[#0F172A]">
            <span className="mask-line">
              <motion.span variants={line} initial="hidden" animate="show" custom={0}>
                {t("hero.title_line1")}
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span variants={line} initial="hidden" animate="show" custom={1}>
                <em className="italic font-light">{t("hero.title_line2")}</em>
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span variants={line} initial="hidden" animate="show" custom={2} className="text-[#D97706]">
                {t("hero.title_line3")}
              </motion.span>
            </span>
          </h1>

          <div className="lg:col-span-4 flex flex-col gap-6">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="text-[#525866] text-base leading-relaxed max-w-md"
            >
              {t("hero.subtitle")}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75 }}
              className="flex flex-wrap items-center gap-3"
            >
              <a href="#opportunities" className="btn-pill btn-pill--dark" data-testid="hero-cta-primary">
                {t("hero.cta_primary")} <span aria-hidden>→</span>
              </a>
              <a href="#contact" className="btn-pill btn-pill--ghost" data-testid="hero-cta-secondary">
                {t("hero.cta_secondary")}
              </a>
            </motion.div>
          </div>
        </div>

        {/* Photo + stats */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-16 lg:mt-24">
          <div className="lg:col-span-8" ref={cardRef} onMouseMove={onMouseMove} onMouseLeave={onLeave}>
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.4, ease: [0.2, 0.7, 0.2, 1] }}
              className="hero-photo relative aspect-[16/11] w-full"
              style={{
                transform: `perspective(1200px) rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)`,
                transition: "transform 240ms ease-out",
              }}
              data-testid="hero-image"
            >
              <img
                src={HERO_IMG}
                alt={t("hero.hero_image_alt")}
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute left-6 top-6 z-10 text-[#FAF9F6] font-mono-mark text-[0.65rem] tracking-[0.3em] uppercase">
                Puerto Madero · 34.6°S 58.3°W
              </div>
              <div className="absolute right-6 bottom-6 z-10 text-[#FAF9F6] text-right">
                <div className="font-serif-display text-3xl">Torre Madero</div>
                <div className="font-mono-mark text-[0.7rem] tracking-[0.2em] uppercase opacity-80">
                  32 pisos · 2024
                </div>
              </div>
            </motion.div>
          </div>
          <div className="lg:col-span-4 flex flex-col justify-between gap-8">
            {[
              { v: t("hero.stat_1_value"), l: t("hero.stat_1_label") },
              { v: t("hero.stat_2_value"), l: t("hero.stat_2_label") },
              { v: t("hero.stat_3_value"), l: t("hero.stat_3_label") },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: 0.1 * i }}
                className="border-t border-slate-900/15 pt-5"
              >
                <div className="font-serif-display text-4xl tracking-tight">{s.v}</div>
                <div className="eyebrow mt-2">{s.l}</div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="mt-14 flex items-center gap-3 text-[#0F172A]/55 font-mono-mark text-[0.68rem] tracking-[0.3em] uppercase">
          <div className="h-px w-10 bg-[#0F172A]/25" />
          {t("hero.scroll_hint")}
        </div>
      </div>
    </section>
  );
}
